import { Space, Typography } from 'antd'
import { observer } from 'mobx-react-lite'

import { OffersCountBadge } from '@/Containers/TransportationSection/Components'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { EntityHeader } from '@/Ship/Components/EntityCard'
import { StatusTag } from '@/Ship/Components/StatusTag'

import { REQUEST_FIELD_LABELS as L } from '../Contracts/RequestFieldLabels'

import type { TransportRequestRelationRef, TransportRequestStatusRef } from '@/Containers/TransportationSection/Components'
import type { RequestFormContextLike } from '../Contracts/IRequest'
import type { ReactNode } from 'react'

/** Ключ ресурсу картки клієнта — відкривається другою шухлядою, без імпорту контейнера. */
const CLIENT_RESOURCE = 'Crm.Client'

const uuidOf = (ref: TransportRequestRelationRef | null | undefined): string | null => {
    if (typeof ref?.uuid === 'string' && ref.uuid !== '') return ref.uuid

    const iri = ref?.['@id']

    return typeof iri === 'string' && iri !== '' ? (iri.split('/').filter(Boolean).pop() ?? null) : null
}

/**
 * Статус бейджем кольору довідника і поруч — бейдж «Кількість пропозицій»,
 * якщо вони є (`SPEC-022` §8.1; легасі `TransformOffersCountTableColumnValueTask.jsx`).
 */
export const RequestStatus = ({ status, countOffers }: { status: TransportRequestStatusRef | null | undefined; countOffers?: unknown }) =>
    status && typeof status.title === 'string' ? (
        <Space size={6}>
            <StatusTag label={status.title} color={status.color} />
            <OffersCountBadge count={countOffers} />
        </Space>
    ) : (
        <>—</>
    )

/**
 * Назва клієнта — посиланням на його картку (`SPEC-022` §8.1, §8.3; легасі
 * `RequestCrud.jsx:54-64`, `RequestFormTemplate.jsx:28-43`). Картка
 * відкривається другою шухлядою поверх списку чи запиту; клік не доходить до
 * рядка таблиці.
 */
export const RequestClientLink = ({ client }: { client: TransportRequestRelationRef | null | undefined }) => {
    const uuid = uuidOf(client)
    const title = typeof client?.title === 'string' && client.title !== '' ? client.title : '—'

    if (uuid === null) return <>{title}</>

    return (
        <Typography.Link
            onClick={(event) => {
                event.stopPropagation()
                OpenResourceCardTask({
                    resource: CLIENT_RESOURCE,
                    mode: 'view',
                    displayMode: 'drawer',
                    primaryFieldValue: uuid,
                })
            }}
            onDoubleClick={(event) => event.stopPropagation()}
        >
            {title}
        </Typography.Link>
    )
}

/**
 * Поле картки в перегляді — та сама розмітка, що в Ship-шаблонів поля: підпис
 * там само, де в сусідніх полів, і e2e знаходить поле за підписом.
 */
export const ViewCell = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="form-field-view">
        <div className="field-view-label">{label}</div>
        <div className="field-view-value">{children}</div>
    </div>
)

/** «Статус» у блоці «Загальне» — лише перегляд: ставлять Action і переходи, не форма. */
export const RequestStatusCell = observer(({ formContext }: { formContext: RequestFormContextLike }) => {
    const values = formContext.values ?? {}
    const tag = <RequestStatus status={values.status} countOffers={values['countOffers']} />

    if (formContext.isEditable !== true) return <ViewCell label={L.status}>{tag}</ViewCell>

    return (
        <div className="form-field form-field-default">
            <label className="field-label">
                <h2 className="field-label-title">{L.status}</h2>
            </label>
            <div>{tag}</div>
        </div>
    )
})

/** «Клієнт» у перегляді — посилання на картку клієнта. */
export const RequestClientViewCell = observer(({ formContext }: { formContext: RequestFormContextLike }) => (
    <ViewCell label={L.client}>
        <RequestClientLink client={formContext.values?.client} />
    </ViewCell>
))

/**
 * Шапка картки: «Запит: <клієнт>» (легасі `RequestResource.jsx:15-26`) і статус
 * бейджем. Окремий observer-компонент: читає значення, які редагують, а
 * ядровий `FormField` на кожне перемальовування шаблону перемонтовував би поля.
 */
export const RequestCardHeader = observer(({ formContext }: { formContext: RequestFormContextLike }) => {
    const values = formContext.values ?? {}
    const status = values.status
    const client = typeof values.client?.title === 'string' && values.client.title !== '' ? values.client.title : null

    return (
        <EntityHeader
            {...(typeof values.id === 'number' ? { code: `Ід ${values.id}` } : {})}
            title={client ? `Запит: ${client}` : 'Запит'}
            status={
                status && typeof status.title === 'string'
                    ? { label: status.title, ...(status.color ? { color: status.color } : {}) }
                    : undefined
            }
        />
    )
})
